import { Shield, Key, Filter, Lock, FileCode, AlertCircle } from "lucide-react";

export const SecureCodingBestPractices = () => {
  const practices = [
    {
      icon: Filter,
      title: "Input Validation",
      description: "Never trust user input. Validate everything on the server side.",
      code: `// TypeScript with Zod
import { z } from 'zod';

const userSchema = z.object({
  email: z.string().email().max(255),
  password: z.string().min(8).max(100),
  age: z.number().int().min(13).max(120),
});

// Validate before processing
const result = userSchema.safeParse(input);
if (!result.success) {
  throw new ValidationError(result.error);
}`,
    },
    {
      icon: Key,
      title: "Password Handling",
      description: "Never store plaintext passwords. Use bcrypt or Argon2.",
      code: `// Using bcrypt
import bcrypt from 'bcrypt';

// Hashing
const saltRounds = 12;
const hash = await bcrypt.hash(password, saltRounds);

// Verification
const isValid = await bcrypt.compare(
  inputPassword, 
  storedHash
);`,
    },
    {
      icon: Lock,
      title: "Parameterized Queries",
      description: "Prevent SQL injection with prepared statements.",
      code: `// BAD - SQL Injection vulnerable
const query = \`SELECT * FROM users 
  WHERE id = '\${userId}'\`;

// GOOD - Parameterized
const { data } = await supabase
  .from('users')
  .select('*')
  .eq('id', userId);

// Or with raw SQL
const result = await db.query(
  'SELECT * FROM users WHERE id = $1',
  [userId]
);`,
    },
    {
      icon: Shield,
      title: "CSRF Protection",
      description: "Protect forms from cross-site request forgery.",
      code: `// Generate token
const csrfToken = crypto
  .randomBytes(32)
  .toString('hex');

// Store in session
req.session.csrfToken = csrfToken;

// Validate on POST
if (req.body.csrf !== req.session.csrfToken) {
  throw new ForbiddenError('Invalid CSRF token');
}`,
    },
    {
      icon: FileCode,
      title: "Output Encoding",
      description: "Prevent XSS by encoding output in the correct context.",
      code: `// React automatically escapes
<div>{userInput}</div>  // Safe

// Danger zone - avoid unless necessary
<div dangerouslySetInnerHTML={{
  __html: DOMPurify.sanitize(userInput)
}} />

// URLs need validation
const isValidUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch { return false; }
};`,
    },
    {
      icon: AlertCircle,
      title: "Error Handling",
      description: "Don't leak sensitive information in error messages.",
      code: `// BAD - Leaks info
catch (error) {
  res.json({ 
    error: error.message,
    stack: error.stack  // Never!
  });
}

// GOOD - Generic message
catch (error) {
  console.error('Auth error:', error); // Log internally
  res.status(401).json({ 
    error: 'Authentication failed' 
  });
}`,
    },
  ];

  return (
    <section className="py-8 sm:py-12">
      <h2 className="text-2xl sm:text-3xl font-display font-bold text-foreground mb-6">
        Secure Coding Patterns
      </h2>
      <p className="text-muted-foreground mb-8 max-w-3xl">
        Practical code examples implementing security best practices. Copy and adapt for your projects.
      </p>

      <div className="grid lg:grid-cols-2 gap-6">
        {practices.map((practice, index) => (
          <div
            key={index}
            className="bg-card rounded-xl border border-border overflow-hidden"
          >
            <div className="p-5 border-b border-border">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <practice.icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground">{practice.title}</h3>
              </div>
              <p className="text-sm text-muted-foreground">{practice.description}</p>
            </div>
            <div className="p-4 overflow-x-auto rounded-b-xl" style={{ backgroundColor: 'hsl(220 14% 93%)', border: '1px solid hsl(220 13% 88%)' }}>
              <pre className="text-xs whitespace-pre-wrap" style={{ color: 'hsl(222 47% 20%)' }}>
                <code style={{ backgroundColor: 'transparent' }}>{practice.code}</code>
              </pre>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
